import React, { useCallback, useEffect, useRef, useState } from 'react'
import type { Paragraph } from '@/api/types/stowed-element'
import type { ClientPoint } from '@/components/app/shapes/shape-text-editor/types/client-point'
import type { ShapeData } from '@/lib/shapes/types/shape-data'
import type { TextRunStyleUpdate } from '@/lib/text/types/text-run-style-update'
import {
  type ShapeTextLayoutResult,
  caretIndexAtShapeLocalPoint,
  paragraphsToPlainText,
} from '@/lib/shapes/shape-text'
import { applyTextareaEditToParagraphs } from '@/lib/shapes/shape-text-edits'
import { clientPointToShapeLocal } from '@/lib/shapes/shape-text-hit'

/**
 * Extra context for a paragraph write, passed **only when there is something to say** — the
 * two-argument call shape is what every non-keystroke caller (style applies, paste, `replaceRange`)
 * uses and what two existing tests assert.
 */
export interface ShapeTextParagraphsChangeOptions {
  /**
   * This write came from an in-flight IME composition (`InputEvent.isComposing`).
   *
   * The owner suppresses the live chain projection for it: that projection rewrites this frame's
   * model to its trimmed capacity, `ShapeTextEditor`'s trim-resync then reassigns the textarea's
   * value to follow, and reassigning `value` mid-composition tears down the composition session — the
   * user loses the characters they are still assembling. The debounced commit picks the reflow up
   * once composition ends.
   */
  isComposing?: boolean
  /**
   * Skip the 500ms debounced commit and push this write's undo entry immediately. For a discrete,
   * single-keystroke style change (e.g. the Up/Down font-size step over a real selection) rather
   * than a run of keystrokes that should coalesce into one undo step — without this, Ctrl+Z right
   * after the step finds nothing on the stack yet, since `commitShapeTextNow` hasn't fired.
   */
  commitImmediately?: boolean
}

interface UseShapeTextEditingOptions {
  shape: ShapeData
  paragraphs: Paragraph[]
  isEditing: boolean
  initialCaretClientPoint?: ClientPoint | null
  /**
   * Character offset (shape-text convention — the one `textValue` itself is in) to place the caret
   * at on entry. Takes precedence over `initialCaretClientPoint`: a chain hand-off knows the exact
   * position it wants, whereas a click point has to be resolved through the layout (WMPCM-9070).
   */
  initialCaretOffset?: number | null
  /** Canvas zoom (doc px → screen px). Used to undo scale/rotation when mapping the click to a caret. */
  canvasZoom: number
  layout: ShapeTextLayoutResult
  textValue: string
  setTextValue: React.Dispatch<React.SetStateAction<string>>
  onParagraphsChange: (
    shapeId: string,
    paragraphs: Paragraph[],
    options?: ShapeTextParagraphsChangeOptions
  ) => void
  onExitEdit: (shapeId: string, opts?: { activateSelectTool?: boolean }) => void
  pendingRunStyleRef: React.MutableRefObject<TextRunStyleUpdate | null>
  textareaRef: React.MutableRefObject<HTMLTextAreaElement | null>
  overlayRef: React.MutableRefObject<HTMLDivElement | null>
  caretInitializedRef: React.MutableRefObject<boolean>
  isEditingRef: React.MutableRefObject<boolean>
  setCaretIndex: React.Dispatch<React.SetStateAction<number>>
  setSelectionEndIndex: React.Dispatch<React.SetStateAction<number>>
  suppressExitOnBlur?: boolean
}

export interface UseShapeTextEditingResult {
  isFocused: boolean
  setIsFocused: React.Dispatch<React.SetStateAction<boolean>>
  handleTextChange: (event: React.ChangeEvent<HTMLTextAreaElement>) => void
  handleBlur: (event: React.FocusEvent<HTMLTextAreaElement>) => void
}

export function useShapeTextEditing({
  shape,
  paragraphs,
  isEditing,
  initialCaretClientPoint,
  initialCaretOffset,
  canvasZoom,
  layout,
  textValue,
  setTextValue,
  onParagraphsChange,
  onExitEdit,
  pendingRunStyleRef,
  textareaRef,
  overlayRef,
  caretInitializedRef,
  isEditingRef,
  setCaretIndex,
  setSelectionEndIndex,
  suppressExitOnBlur = false,
}: UseShapeTextEditingOptions): UseShapeTextEditingResult {
  const [isFocused, setIsFocused] = useState(false)
  const suppressExitOnBlurRef = useRef(suppressExitOnBlur)

  suppressExitOnBlurRef.current = suppressExitOnBlur

  useEffect(() => {
    if (!isEditing) {
      caretInitializedRef.current = false
      setIsFocused(false)
      pendingRunStyleRef.current = null
    }
  }, [caretInitializedRef, isEditing, pendingRunStyleRef])

  useEffect(() => {
    if (!isEditing || caretInitializedRef.current) return
    const textarea = textareaRef.current
    const host = overlayRef.current

    if (!textarea || !host) return

    caretInitializedRef.current = true
    textarea.focus({ preventScroll: true })

    const point = initialCaretClientPoint ?? null
    let index = textValue.length

    if (initialCaretOffset != null) {
      // Clamped because the offset was computed by the caller from the paragraphs it wrote, and a
      // reflow between that write and this mount can shorten the frame's text.
      //
      // Measured against the MODEL, not `textValue`. On a chain hand-off this frame enters edit in
      // the very commit that rewrote its paragraphs, and `use-shape-text-layout`'s
      // paragraphs-to-`textValue` sync bails on exactly that commit (`if (isEditing) return`) — so
      // `textValue` here still describes the PRE-reflow string. Clamping to it truncates a hand-off
      // into a frame the reflow just made longer, which is the same class of miss as the one the
      // trim-resync in `shape-text-editor.tsx` had. `paragraphsToPlainText` is the convention the
      // caller measured its offset with (`measureShapeParagraphsPlainTextLength`), so the two agree
      // on one number rather than two.
      index = Math.max(0, Math.min(initialCaretOffset, paragraphsToPlainText(paragraphs).length))
    } else if (point) {
      const local = clientPointToShapeLocal(
        point.x,
        point.y,
        host.getBoundingClientRect(),
        canvasZoom,
        shape.rotation ?? 0,
        shape.width,
        shape.height
      )

      index = caretIndexAtShapeLocalPoint(layout.carets, local.x, local.y)
    }

    // Synchronously FIRST, not only from the frame below (WMPCM-9070, held-key repeat). This effect
    // is what focuses the textarea, so between the `focus()` above and the callback below there is a
    // focused, editable frame whose caret is still at offset 0. A chain hand-off enters that state
    // mid-keystroke — the caret followed its text into this frame — and the ~30 ms auto-repeat of a
    // held Space or Backspace lands inside the gap, editing at the frame's start instead of where the
    // hand-off put the caret.
    textarea.setSelectionRange(index, index)
    setCaretIndex(index)
    setSelectionEndIndex(index)

    // Kept as a re-assert for the case the synchronous call cannot cover: a `value` write landing
    // after this effect (React's own, or the trim-resync's re-assert) collapses the selection to the
    // end of the text.
    const valueAtInit = textarea.value

    requestAnimationFrame(() => {
      // Only while nothing has been typed since. Re-asserting over a real keystroke would drag the
      // caret back to where the session opened — the same held-key window, from the other side.
      if (textarea.value !== valueAtInit) return

      textarea.setSelectionRange(index, index)
      setCaretIndex(index)
      setSelectionEndIndex(index)
    })
  }, [
    canvasZoom,
    caretInitializedRef,
    initialCaretClientPoint,
    initialCaretOffset,
    isEditing,
    layout.carets,
    overlayRef,
    paragraphs,
    setCaretIndex,
    setSelectionEndIndex,
    shape.height,
    shape.rotation,
    shape.width,
    textValue.length,
    textareaRef,
  ])

  const handleTextChange = useCallback(
    (event: React.ChangeEvent<HTMLTextAreaElement>) => {
      const textarea = event.currentTarget
      const next = textarea.value
      // Read off the native event rather than tracked with composition listeners, and passed only
      // when true so every other caller keeps the two-argument shape — see
      // `ShapeTextParagraphsChangeOptions`.
      const isComposing = (event.nativeEvent as InputEvent | undefined)?.isComposing === true
      const rawSelection = textarea.selectionStart ?? next.length
      const selection = Math.max(0, Math.min(rawSelection, next.length))

      // The caret ANCHORS the edit, and `textValue` is the string it is measured against — the
      // textarea is controlled, so React guarantees the node held `props.value` before this
      // keystroke. This used to be a bare common-prefix/common-suffix diff, which reproduces the
      // new string but cannot say WHERE among several equally valid edits the user typed, and
      // resolves the ambiguity as far right as the repeated characters allow. In a linked chain that
      // deleted the frame's LAST character while the one before the caret survived. See
      // `resolveTextareaEdit`, which verifies its own premise and falls back to the old affix diff
      // whenever the caret cannot explain the change.
      //
      // Withheld only for an in-flight composition, which replaces a segment the caret does not
      // bound. Deliberately NOT also gated on the selection being collapsed: a post-edit selection
      // is collapsed for every edit this surface produces — including type-over and
      // Backspace-over-a-range, where the caret lands at the end of what was inserted — so such a
      // guard would be true on every real keystroke while giving one more boolean the chance to be
      // quietly false and make this whole fix inert (CLAUDE.md's inert-guard class, 11 outages).
      // `resolveTextareaEdit`'s own replay is the safety net, and it is a stronger one: it rejects a
      // caret that cannot explain the change, whatever the reason.
      const caretAnchor = isComposing ? null : selection

      // Over-capacity input is ACCEPTED, never discarded (WMPCM-9338). The scanline engine simply
      // stops laying out past the last fitting row (`fitParagraphsToShape`), and everything beyond
      // `consumedLength` is carried as overset: kept in `paragraphs`, persisted to the wire as
      // `isHidden` lines (`bakeShapeTextWireLines`), read back on load, and re-flowed into view the
      // moment the frame grows. The overflow badge is what tells the user text is being withheld.
      //
      // This replaces the WMPCM-8588 hard-block, which rejected such input outright so that the word
      // being typed could not visibly vanish. That traded a cosmetic problem for silent data loss:
      // moving the caret back into a full frame and typing dropped every keystroke, so expanding the
      // frame afterwards had nothing to reveal. Legacy/yb360 persists overset instead of refusing it
      // (`HDN="true"` rows, `sprite.content.overflowText`), and WMPCM-9338 rules the same way:
      // "all text entered by the user should be preserved". Paste and IME never honoured the block
      // anyway, so dropping it also makes typing, pasting and composing behave identically.
      //
      // `applyTextareaEditToParagraphs` also owns the invariant the inline diff left implicit: these
      // offsets are resolved in `textValue` space and applied in MODEL space, and in a linked chain
      // the live projection rewrites the model behind this editor between keystrokes. It reconciles
      // its result against `next`, so the model describes exactly what the user sees typed even when
      // the trim-resync in `shape-text-editor.tsx` has not caught up yet.
      const nextParagraphs = applyTextareaEditToParagraphs(
        paragraphs,
        textValue,
        next,
        caretAnchor,
        pendingRunStyleRef.current
      )

      setTextValue(next)

      if (isComposing) onParagraphsChange(shape.id, nextParagraphs, { isComposing: true })
      else onParagraphsChange(shape.id, nextParagraphs)

      setCaretIndex(selection)
      setSelectionEndIndex(selection)
    },
    [
      onParagraphsChange,
      paragraphs,
      pendingRunStyleRef,
      setCaretIndex,
      setSelectionEndIndex,
      setTextValue,
      shape.id,
      textValue,
    ]
  )

  const handleBlur = useCallback(
    (event: React.FocusEvent<HTMLTextAreaElement>) => {
      setIsFocused(false)
      const relatedTarget = event.relatedTarget as HTMLElement | null

      requestAnimationFrame(() => {
        if (!isEditingRef.current) return
        if (suppressExitOnBlurRef.current) return

        const textarea = textareaRef.current

        if (textarea && document.activeElement === textarea) return

        const focusedEl = relatedTarget ?? (document.activeElement as HTMLElement | null)

        if (focusedEl?.closest?.('[data-text-edit-keepalive]')) return

        onExitEdit(shape.id)
      })
    },
    [isEditingRef, onExitEdit, shape.id, textareaRef]
  )

  return { isFocused, setIsFocused, handleTextChange, handleBlur }
}
